import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Empty,
  List,
  Modal,
  Radio,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  BellOutlined,
  EyeInvisibleOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { trpc } from '../lib/api';
import { PageHeader, PageContainer } from '../components/common';
import { HelpButton } from '../components/help/HelpButton';
import { useResponsive } from '../hooks/useResponsive';
import { getMutationPendingState } from '../utils/trpcMutation';

type UserNotificationStatus = 'unread' | 'read' | 'dismissed';

interface NotificationListItem {
  id: string;
  title: string;
  bodyMd: string | null;
  status: UserNotificationStatus;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

const STATUS_OPTIONS: Array<{ label: string; value: 'all' | UserNotificationStatus }> = [
  { label: 'すべて', value: 'all' },
  { label: '未読', value: 'unread' },
  { label: '既読', value: 'read' },
  { label: '非表示', value: 'dismissed' },
];

const LIMIT = 20;

export function NotificationsPage() {
  const [statusFilter, setStatusFilter] = useState<'all' | UserNotificationStatus>('unread');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const utils = trpc.useContext();
  const { isMobile } = useResponsive();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = trpc.notifications.list.useInfiniteQuery(
    {
      limit: LIMIT,
      status: statusFilter === 'all' ? undefined : [statusFilter],
      includeDismissed: statusFilter === 'dismissed',
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      refetchOnWindowFocus: false,
    }
  );

  const markReadMutation = trpc.notifications.markAsRead.useMutation({
    onSuccess: async () => {
      setSelectedIds([]);
      await utils.notifications.list.invalidate();
    },
    onError: (error) => messageApi.error(error.message ?? '通知の更新に失敗しました'),
  });

  const markUnreadMutation = trpc.notifications.markAsUnread.useMutation({
    onSuccess: async () => {
      setSelectedIds([]);
      await utils.notifications.list.invalidate();
    },
    onError: (error) => messageApi.error(error.message ?? '通知の更新に失敗しました'),
  });

  const dismissMutation = trpc.notifications.dismiss.useMutation({
    onSuccess: async () => {
      setSelectedIds([]);
      await utils.notifications.list.invalidate();
    },
    onError: (error) => messageApi.error(error.message ?? '通知の非表示に失敗しました'),
  });

  const deleteMutation = trpc.notifications.delete.useMutation({
    onSuccess: async () => {
      setSelectedIds([]);
      await utils.notifications.list.invalidate();
    },
    onError: (error) => messageApi.error(error.message ?? '通知の削除に失敗しました'),
  });

  const markAllMutation = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: async () => {
      setSelectedIds([]);
      await utils.notifications.list.invalidate();
      messageApi.success('すべての通知を既読にしました');
    },
    onError: (error) => messageApi.error(error.message ?? '通知の更新に失敗しました'),
  });

  const items: NotificationListItem[] = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.items ?? []) ?? []).map((item) => ({
        id: item.id ?? '',
        title: item.title ?? '',
        bodyMd: item.bodyMd ?? null,
        status: (item.status as UserNotificationStatus | undefined) ?? 'unread',
        createdAt: item.createdAt ?? '',
        metadata:
          item.metadata && typeof item.metadata === 'object' && !Array.isArray(item.metadata)
            ? (item.metadata as Record<string, unknown>)
            : null,
      })),
    [data]
  );

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  const handleBulkRead = async () => {
    if (!selectedIds.length) return;
    await Promise.all(
      selectedIds.map((id) => markReadMutation.mutateAsync({ notificationId: id }))
    );
    messageApi.success('選択した通知を既読にしました');
  };

  const handleBulkUnread = async () => {
    if (!selectedIds.length) return;
    await Promise.all(
      selectedIds.map((id) => markUnreadMutation.mutateAsync({ notificationId: id }))
    );
    messageApi.success('選択した通知を未読にしました');
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    await Promise.all(
      selectedIds.map((id) => deleteMutation.mutateAsync({ notificationId: id }))
    );
    messageApi.success('選択した通知を削除しました');
  };

  const handleMarkAllAsRead = () => {
    Modal.confirm({
      title: '一括既読',
      content: 'このフォルダー内の通知をすべて既読にします。よろしいですか？',
      okText: '既読にする',
      cancelText: 'キャンセル',
      onOk: () => markAllMutation.mutateAsync(),
    });
  };

  const renderStatusTag = (status: UserNotificationStatus) => {
    switch (status) {
      case 'unread':
        return <Tag color="blue">未読</Tag>;
      case 'read':
        return <Tag color="green">既読</Tag>;
      case 'dismissed':
        return <Tag color="default">非表示</Tag>;
      default:
        return null;
    }
  };

  const isBulkDisabled =
    !selectedIds.length ||
    getMutationPendingState(markReadMutation) ||
    getMutationPendingState(markUnreadMutation) ||
    getMutationPendingState(dismissMutation) ||
    getMutationPendingState(deleteMutation);

  const buttonSize = isMobile ? 'small' : 'middle';

  const renderItemActions = (item: NotificationListItem) => {
    const actions: React.ReactNode[] = [];

    if (item.status === 'unread') {
      actions.push(
        <Button
          key="single-mark-read"
          type="link"
          size="small"
          onClick={() => void markReadMutation.mutateAsync({ notificationId: item.id })}
        >
          既読にする
        </Button>
      );
    }

    if (item.status === 'read') {
      actions.push(
        <Button
          key="single-mark-unread"
          type="link"
          size="small"
          onClick={() => void markUnreadMutation.mutateAsync({ notificationId: item.id })}
        >
          未読にする
        </Button>
      );
    }

    if (item.status !== 'dismissed') {
      actions.push(
        <Button
          key="single-dismiss"
          type="link"
          size="small"
          icon={<EyeInvisibleOutlined />}
          onClick={() => void dismissMutation.mutateAsync({ notificationId: item.id })}
        >
          非表示
        </Button>
      );
    }

    if (item.status === 'dismissed') {
      actions.push(
        <Button
          key="single-undismiss"
          type="link"
          size="small"
          icon={<UndoOutlined />}
          onClick={() => void markUnreadMutation.mutateAsync({ notificationId: item.id })}
        >
          非表示を解除
        </Button>
      );
    }

    actions.push(
      <Button
        key="single-delete"
        type="link"
        danger
        size="small"
        onClick={() => void deleteMutation.mutateAsync({ notificationId: item.id })}
      >
        削除
      </Button>
    );

    return actions;
  };

  return (
    <>
      {contextHolder}
      <PageContainer>
        <PageHeader
          title="通知受信箱"
          subtitle="パートナー操作やシステムから届く通知を確認・整理できます。"
          icon={<BellOutlined />}
          extra={<HelpButton topicId="notifications-inbox" />}
        />
        <Card>
          <Space
            direction="vertical"
            style={{ width: '100%', marginBottom: 16 }}
            size="large"
          >
            <Space wrap>
              <Space>
                <Typography.Text>表示フィルター</Typography.Text>
                <Radio.Group
                  aria-label="通知ステータス"
                  options={STATUS_OPTIONS}
                  optionType="button"
                  size={buttonSize}
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setSelectedIds([]);
                  }}
                />
              </Space>
              <Button
                icon={<ReloadOutlined />}
                size={buttonSize}
                onClick={() => refetch()}
                aria-label="最新の情報に更新"
              >
                更新
              </Button>
            </Space>

            <Space wrap>
              {statusFilter !== 'read' && statusFilter !== 'dismissed' && (
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  ghost
                  size={buttonSize}
                  disabled={isBulkDisabled}
                  onClick={() => void handleBulkRead()}
                >
                  選択を既読
                </Button>
              )}
              {statusFilter !== 'unread' && statusFilter !== 'dismissed' && (
                <Button
                  icon={<UndoOutlined />}
                  size={buttonSize}
                  disabled={isBulkDisabled}
                  onClick={() => void handleBulkUnread()}
                >
                  選択を未読
                </Button>
              )}
              <Button
                icon={<DeleteOutlined />}
                danger
                size={buttonSize}
                disabled={isBulkDisabled}
                onClick={() => void handleBulkDelete()}
              >
                選択を削除
              </Button>
              {statusFilter !== 'dismissed' && (
                <Button
                  icon={<CheckOutlined />}
                  size={buttonSize}
                  onClick={handleMarkAllAsRead}
                  loading={getMutationPendingState(markAllMutation)}
                >
                  すべて既読
                </Button>
              )}
              <Typography.Text type="secondary">
                選択数: {selectedIds.length}
              </Typography.Text>
            </Space>
          </Space>

          {isLoading ? (
            <Spin />
          ) : items.length === 0 ? (
            <Empty description="通知はありません" />
          ) : (
            <List
              itemLayout="vertical"
              dataSource={items}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  actions={renderItemActions(item)}
                >
                  <List.Item.Meta
                    avatar={
                      <Checkbox
                        aria-label={`通知選択-${item.id}`}
                        checked={selectedIds.includes(item.id)}
                        onChange={(event) => handleSelect(item.id, event.target.checked)}
                      />
                    }
                    title={
                      <Space size="middle">
                        {renderStatusTag(item.status)}
                        <Typography.Text strong>{item.title}</Typography.Text>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {item.bodyMd && (
                          <Typography.Paragraph ellipsis={{ rows: 2 }}>
                            {item.bodyMd}
                          </Typography.Paragraph>
                        )}
                        <Space size="small">
                          <ClockCircleOutlined />
                          <Typography.Text type="secondary">
                            {new Date(item.createdAt).toLocaleString('ja-JP')}
                          </Typography.Text>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}

          {hasNextPage && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetchingNextPage}
              >
                さらに読み込む
              </Button>
            </div>
          )}
        </Card>
      </PageContainer>
    </>
  );
}
